import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import { useUser } from "../context/UserContext"; // Make sure user context is working
import "./Users.css";

const Users = () => {
  const { user } = useUser(); // user.organizationId must exist
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const calculateAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    if (!user || !user.organizationId) {
      console.warn("No organizationId in user");
      return;
    }

    const q = query(
      collection(db, "users"),
      where("organizationId", "==", user.organizationId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const userList = snapshot.docs
          .map((doc, index) => {
            const data = doc.data();
            return {
              id: doc.id,
              sno: index + 1,
              firstName: data.firstName || "",
              lastName: data.lastName || "",
              email: data.email || "",
              designation: data.designation || "",
              dob: data.dob || "",
              age: calculateAge(data.dob),
            };
          })
          .filter((user) => user.firstName.trim());

        setUsers(userList);
        setSnackbar({ open: false, message: "" });

      },
      (error) => {
        console.error("Error fetching users:", error);
        setSnackbar({ open: true, message: "Failed to fetch users." });
      }
    );

    return () => unsubscribe();
  }, [user]);

  const handleSearch = (e) => setSearchText(e.target.value);

  const filteredUsers = users
    .filter(
      (user) =>
        user.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email.toLowerCase().includes(searchText.toLowerCase())
    )
    .map((user, index) => ({ ...user, sno: index + 1 }));

  const columns = [
    { field: "sno", headerName: "S.No.", width: 60 },
    { field: "firstName", headerName: "First Name", flex: 1 },
    { field: "lastName", headerName: "Last Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.5 },
    { field: "designation", headerName: "Designation", flex: 1 },
    { field: "age", headerName: "Age", width: 80 },
  ];

  return (
    <Box p={4} sx={{ width: "100%", boxSizing: "border-box", overflowX: "auto" }}>
      <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <TextField
            value={searchText}
            onChange={handleSearch}
            placeholder="Search users"
            size="small"
            sx={{ width: 200, backgroundColor: "rgba(255,255,255,0.3)" }}
          />
        </Box>

        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontFamily: "Lato, sans-serif",
            color: "#645792",
            fontWeight: "bold",
          }}
        >
          Users
        </Typography>

        <Box
          sx={{
            width: "100%",
            transition: "all 0.3s ease",
            overflow: "hidden",
            flexGrow: 1,
          }}
        >
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            className="grid"
            autoHeight
            disableRowSelectionOnClick
            sx={{
              mt: 3,
              borderRadius: 2,
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "rgba(180, 87, 146,0.1)",
              },
              "& .MuiDataGrid-row": {
                borderBottom: "1px solid rgba(0,0,0,0.1)",
              },
            }}
          />
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity="info"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Users;
